import { Component, effect, inject, input, output, signal } from '@angular/core';
import {
  ActionListItemModel,
  ActionMenuItemAction,
  BadgeColor,
  CapitalizePipe,
  clearCustomFilters,
  ClientOrder,
  DateFilterOperation,
  getParamValue,
  HydraCollectionResponse,
  KendoGridComponent,
  ListSkeletonComponent,
  onPageChange,
  RcDateRangeFilterComponent,
  setCustomBetweenFilter,
  setCustomFilter,
  SupplierInvoiceSection,
} from 'shared';
import { HttpClient } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import {
  CellClickEvent,
  CellTemplateDirective,
  ColumnComponent,
  FilterCellTemplateDirective,
  GridComponent,
  GridDataResult,
  LoadingTemplateDirective,
  NoRecordsTemplateDirective,
} from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { environment } from '@environments';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { CheckBoxComponent, CheckBoxState, FormFieldComponent, TextBoxComponent } from '@progress/kendo-angular-inputs';
import { distinctUntilChanged } from 'rxjs/operators';
import { LabelComponent } from '@progress/kendo-angular-label';
import { UpperCasePipe } from '@angular/common';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'rc-client-order-table',
  standalone: true,
  templateUrl: './client-order-table.component.html',
  styleUrl: './client-order-table.component.scss',
  imports: [
    GridComponent,
    ColumnComponent,
    FilterCellTemplateDirective,
    CellTemplateDirective,
    CapitalizePipe,
    NoRecordsTemplateDirective,
    TranslateModule,
    TooltipDirective,
    LoadingTemplateDirective,
    ListSkeletonComponent,
    ListSkeletonComponent,
    TextBoxComponent,
    CheckBoxComponent,
    FormFieldComponent,
    LabelComponent,
    RcDateRangeFilterComponent,
    UpperCasePipe,
    ReactiveFormsModule,
  ],
  providers: [CapitalizePipe, DialogRef, DialogService],
})
export class ClientOrderTableComponent extends KendoGridComponent {
  #httpClient = inject(HttpClient);
  BadgeColor = BadgeColor;
  dialogService = inject(DialogService);
  dialog: DialogRef;

  gridData$: GridDataResult = { total: 0, data: [] };
  clientOrderSelection = output<ClientOrder | undefined>();

  protected readonly DateFilterOperation = DateFilterOperation;
  protected readonly of = of;

  section = input.required<SupplierInvoiceSection>();
  selectedOrder = input<ClientOrder | undefined>(undefined);
  initialValue = input<string>();
  itemsPerPage = input<number>(5);
  params = signal<string[]>([]);
  amountShown = signal<{ current: number; total: number }>({ current: 0, total: 0 });
  paramString = signal<string>('');
  hasAppliedInitial = signal(false);

  filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: 'contains',
  };

  private destroy$ = new Subject<void>();
  private lastKey = '';
  private gridInitialized = false;
  private prevParamsKey = '';

  reload$ = new Subject<void>();

  constructor() {
    super();
    this.isFrontendFilterTable.set(true);

    effect(() => {
      const gridInstance = this.grid();
      const settings = this.userSettings();
      if (!gridInstance || !settings) return;
    });
    effect(() => {
      const ref = this.section()?.reference;
      this.params.set([`reference=${ref}`]);
    });

    effect(() => {
      const key = this.params().join('&');

      const initVal = this.initialValue();
      const sel = this.selectedOrder();

      // const deliveryAddress = this.section()?.deliveryAddress;

      const waitingForInitial = !!initVal && (!sel || (sel && initVal === sel['@id'] && !this.hasAppliedInitial()));

      if (waitingForInitial) return;

      if (key && key !== this.prevParamsKey) {
        this.prevParamsKey = key;
        this.reload$.next();
      }
    });

    this.reload$
      .pipe(
        debounceTime(300),
        map(() => this.params().join('&')),
        distinctUntilChanged(),
        tap((key) => (this.lastKey = key)),
        switchMap(() => this.#getClientOrders()),
        takeUntil(this.destroy$),
      )
      .subscribe((value) => (this.gridData$ = value));

    effect(() => {
      const selection = this.selectedOrder();
      const initialValue = this.initialValue();
      const pageSize = this.itemsPerPage();

      this.params.update((prev) => {
        const upsert = (arr: string[], key: string, value: string) => {
          const entry = `${key}=${value}`;
          const i = arr.findIndex((p) => p.startsWith(`${key}=`));
          if (i === -1) return [...arr, entry];
          if (arr[i] === entry) return arr; // no change
          const next = arr.slice();
          next[i] = entry;
          return next;
        };

        let next = upsert(prev, 'itemsPerPage', String(pageSize));

        if (!this.hasAppliedInitial() && initialValue && selection && initialValue === selection['@id']) {
          next = upsert(next, 'reference', selection.reference);
          next = upsert(next, 'documentNumber', selection.documentNumber);
          this.hasAppliedInitial.set(true);
        }

        const nextString = next.join('&');
        if (nextString !== this.paramString()) {
          this.paramString.set(nextString);
        }

        return next;
      });
    });
  }

  #getClientOrders(): Observable<GridDataResult> {
    this.isLoadingGridData$.next(true);
    const paramString = this.params().join('&');
    return this.#httpClient
      .get<HydraCollectionResponse<ClientOrder>>(`${environment.apiUrl}/client_orders?${paramString}`)
      .pipe(
        map((response) => ({
          total: response['hydra:totalItems'],
          data: [...response['hydra:member']],
        })),
        tap((result) => {
          this.amountShown.set({ current: result.data.length, total: result.total });
        }),
        finalize(() => this.isLoadingGridData$.next(false)),
      );
  }

  onDataStateChange(state: State): void {
    this.gridState = state;
    if (!this.gridInitialized) {
      this.gridInitialized = true;
      return;
    }

    this.reload$.next();
  }

  onGridRowClick(event: CellClickEvent): void {
    if (this.hasClickedGridRowButtons(event) || !this.isLeftClick(event)) {
      return;
    }

    const item = event.dataItem as ClientOrder;
    const currentlySelected = this.selectedOrder()?.id === item.id;

    if (currentlySelected) {
      // unselect
      this.clientOrderSelection.emit(undefined);
      clearCustomFilters(this.itemsPerPage(), this.params, this.reload$);
    } else {
      // select
      this.clientOrderSelection.emit(item);
    }
  }

  removeOrSetSelection(event: CheckBoxState, item: ClientOrder) {
    if (event === true) {
      this.clientOrderSelection.emit(item);
    } else {
      if (item['@id'] === this.selectedOrder()?.['@id']) {
        this.clientOrderSelection.emit(undefined);
        clearCustomFilters(this.itemsPerPage(), this.params, this.reload$);
      }
    }
  }

  protected readonly getParamValue = getParamValue;
  protected readonly clearCustomFilters = clearCustomFilters;
  protected readonly setCustomBetweenFilter = setCustomBetweenFilter;
  protected readonly setCustomFilter = setCustomFilter;
  protected readonly onPageChange = onPageChange;
}
